/**
 * Native Recharts data transformation utilities
 * 
 * This module provides functions to transform application data into 
 * Recharts-compatible formats, focusing on flat arrays of objects.
 */

import { ChartDataPoint } from '../types';

/**
 * Recharts data point (flat object)
 */
export interface RechartsDataPoint {
  [key: string]: string | number | Date;
}

/**
 * Simplified Recharts configuration
 */
export interface RechartsConfig {
  type: 'line' | 'bar' | 'pie' | 'radar' | 'scatter';
  data: RechartsDataPoint[];
  options?: any;
}

/**
 * Transform application ChartDataPoint into a flat RechartsDataPoint
 */
export function transformToRechartsPoint(point: ChartDataPoint): RechartsDataPoint {
  return {
    time: point.time,
    revenue: point.revenue,
    members: point.members,
    activities: point.activities,
    contributions: point.contributions,
    benefits: point.benefits,
    events: point.events,
    expenses: point.expenses,
  };
}

/**
 * Bulk transformation of application data points
 */
export function transformChartDataPoints(
  dataPoints: ChartDataPoint[]
): RechartsDataPoint[] {
  return dataPoints.map(transformToRechartsPoint);
}

/**
 * Generic chart data transformer for Recharts
 */
export function transformChartData(
  chartData: ChartDataPoint[],
  chartType: 'line' | 'bar' | 'pie' | 'radar' | 'scatter'
): RechartsConfig {
  return {
    type: chartType,
    data: transformChartDataPoints(chartData)
  };
}

/**
 * Performance-optimized data transformer for large datasets
 */
export function transformChartDataOptimized(
  chartData: ChartDataPoint[],
  chartType: 'line' | 'bar' | 'pie' | 'radar' | 'scatter',
  options: {
    batchSize?: number;
    enableValidation?: boolean;
  } = {}
): {
  config: RechartsConfig;
  validation?: { isValid: boolean; issues: string[] };
} {
  const { batchSize = 1000, enableValidation = false } = options;

  if (chartData.length > batchSize) {
    console.warn(`Large dataset detected (${chartData.length} points)`);
  }

  const config = transformChartData(chartData, chartType);

  let validation;
  if (enableValidation) {
    // Basic validation: count check
    validation = {
      isValid: chartData.length === config.data.length,
      issues: chartData.length !== config.data.length ? ['Data count mismatch'] : []
    };
  }

  return {
    config,
    validation
  };
}

/**
 * Aggregate time-series data for Recharts
 */
export function aggregateTimeSeriesData(
  data: Array<{ x: string | Date; y: number }>,
  interval: 'hour' | 'day' | 'week' | 'month' | 'year',
  aggregation: 'sum' | 'average' | 'count' = 'sum'
): Array<{ x: string; y: number }> {
  if (!data.length) return [];

  const grouped = new Map<string, number[]>();

  data.forEach(point => {
    const date = point.x instanceof Date ? point.x : new Date(point.x);
    const key = getIntervalKey(date, interval);

    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(point.y);
  });

  const result: Array<{ x: string; y: number }> = [];

  Array.from(grouped.entries()).forEach(([key, values]) => {
    let aggregatedValue: number;

    switch (aggregation) {
      case 'sum':
        aggregatedValue = values.reduce((sum, val) => sum + val, 0);
        break;
      case 'average':
        aggregatedValue = values.reduce((sum, val) => sum + val, 0) / values.length;
        break;
      case 'count':
        aggregatedValue = values.length;
        break;
      default:
        aggregatedValue = values[0] || 0;
    }

    result.push({ x: key, y: aggregatedValue });
  });

  return result.sort((a, b) => a.x.localeCompare(b.x));
}

function getIntervalKey(date: Date, interval: string): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');

  switch (interval) {
    case 'hour': return `${year}-${month}-${day} ${hours}:00`;
    case 'day': return `${year}-${month}-${day}`;
    case 'week':
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      return `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`;
    case 'month': return `${year}-${month}`;
    case 'year': return `${year}`;
    default: return date.toISOString();
  }
}